/**
 * Search Bar Component for v2v2 SWFL Farmers Market Application
 * Full-featured search input with debounce, loading state, and enhanced suggestions
 */



import { useState, useCallback, useRef, useEffect } from 'react';
import { Search, X, Loader2 } from 'lucide-react';
import { useSearch } from '@/hooks/use-search';
import { useSuggestions } from '@/hooks/use-suggestions';
import { SuggestionList } from '@/components/suggestions';
import { cn } from '@/lib/utils';
import type { SearchSuggestion } from '@/types/suggestions';

interface SearchBarProps {
  className?: string;
  placeholder?: string;
  onSearch?: (query: string) => void;
  showSuggestions?: boolean;
  autoFocus?: boolean;
  variant?: 'default' | 'large' | 'compact';
}

export function SearchBar({
  className,
  placeholder = 'Search products, vendors, markets...',
  onSearch,
  showSuggestions = true,
  autoFocus = false,
  variant = 'default',
}: SearchBarProps) {
  const {
    query,
    setQuery,
    isLoading,
    clearSearch,
    isIndexBuilt,
  } = useSearch();

  const {
    suggestions,
    isLoading: isSuggestionsLoading,
    selectedIndex,
    setSelectedIndex,
    onSelect: onSuggestionSelect,
    navigateSuggestions,
  } = useSuggestions(query);

  const [showSuggestionsDropdown, setShowSuggestionsDropdown] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Handle clicking outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowSuggestionsDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle input change
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newQuery = e.target.value;
    setQuery(newQuery);
    setShowSuggestionsDropdown(newQuery.length > 0);
    onSearch?.(newQuery);
  }, [setQuery, onSearch]);

  // Handle clear
  const handleClear = useCallback(() => {
    setQuery('');
    clearSearch();
    setShowSuggestionsDropdown(false);
    inputRef.current?.focus();
  }, [setQuery, clearSearch]);

  // Handle suggestion selection
  const handleSuggestionSelect = useCallback((suggestion: SearchSuggestion) => {
    setQuery(suggestion.text);
    setShowSuggestionsDropdown(false);
    onSearch?.(suggestion.text);
    onSuggestionSelect(suggestion);
    inputRef.current?.blur();
  }, [setQuery, onSearch, onSuggestionSelect]);

  // Handle keyboard navigation for suggestions
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestionsDropdown) {
      if (e.key === 'Escape') {
        setShowSuggestionsDropdown(false);
        inputRef.current?.blur();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        navigateSuggestions('down');
        setShowSuggestionsDropdown(true);
        break;
      case 'ArrowUp':
        e.preventDefault();
        navigateSuggestions('up');
        setShowSuggestionsDropdown(true);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          // Get the selected suggestion
          const allSuggestions = suggestions.flatMap(group => group.suggestions);
          if (selectedIndex < allSuggestions.length) {
            handleSuggestionSelect(allSuggestions[selectedIndex]);
          }
        } else {
          // No suggestion selected, submit the search
          setShowSuggestionsDropdown(false);
          onSearch?.(query);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowSuggestionsDropdown(false);
        inputRef.current?.blur();
        break;
      case 'Home':
        e.preventDefault();
        navigateSuggestions('first');
        break;
      case 'End':
        e.preventDefault();
        navigateSuggestions('last');
        break;
    }
  }, [
    showSuggestionsDropdown,
    selectedIndex,
    suggestions,
    navigateSuggestions,
    handleSuggestionSelect,
    onSearch,
    query
  ]);

  // Focus handler to show suggestions
  const handleFocus = useCallback(() => {
    if (query.length > 0) {
      setShowSuggestionsDropdown(true);
    }
  }, [query.length]);

  // Variant styles
  const variantStyles = {
    default: 'h-10 px-4 text-sm',
    large: 'h-12 px-6 text-base',
    compact: 'h-8 px-3 text-xs',
  };

  // Loading indicator
  const isSearching = isLoading || !isIndexBuilt;

  return (
    <div className={cn('relative', className)} ref={wrapperRef}>
      {/* Search Icon */}
      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
        {isSearching ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Search className="h-4 w-4" />
        )}
      </div>

      {/* Input */}
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        placeholder={!isIndexBuilt ? 'Loading search...' : placeholder}
        disabled={!isIndexBuilt}
        autoFocus={autoFocus}
        aria-autocomplete="list"
        aria-controls="search-suggestions"
        aria-activedescendant={selectedIndex >= 0 ? `suggestion-${selectedIndex}` : undefined}
        className={cn(
          'w-full rounded-lg border border-input bg-background',
          'pl-10 pr-10 ring-offset-background',
          'placeholder:text-muted-foreground',
          'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
          'disabled:cursor-not-allowed disabled:opacity-50',
          variantStyles[variant]
        )}
      />

      {/* Clear Button */}
      {query && (
        <button
          onClick={handleClear}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Clear search"
        >
          <X className="h-4 w-4" />
        </button>
      )}

      {/* Enhanced Suggestions Dropdown */}
      {showSuggestions && (
        <SuggestionList
          groups={suggestions}
          isLoading={isSuggestionsLoading}
          selectedIndex={selectedIndex}
          onSelect={handleSuggestionSelect}
          onClose={() => setShowSuggestionsDropdown(false)}
          query={query}
          isOpen={showSuggestionsDropdown}
        />
      )}

      {/* Active Search Indicator */}
      {query && (
        <div className="absolute -top-2 -right-2">
          <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full bg-primary text-[10px] font-medium text-primary-foreground">
            {query.length > 0 ? '✓' : ''}
          </span>
        </div>
      )}
    </div>
  );
}
